import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { PaziaInput, PaziaTextarea, PaziaLoader, PaziaFormButton } from "components/atoms";
import { useLandingPages, useLandingPage, shopApi } from "api/shop";
import { useToaster } from "utils";
import { ShopSeoCourseGroupsPage } from "./ShopSeoCourseGroupsPage";

const schema = yup.object({
  metaTitle: yup.string().optional().default(""),
  metaDescription: yup.string().optional().default(""),
});

type SeoFormValues = {
  metaTitle?: string;
  metaDescription?: string;
};

const SeoPageRow = ({ pageId, name, slug }: { pageId: number; name: string; slug: string }) => {
  const { landingPage, isLoading, invalidate } = useLandingPage(pageId);
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      metaTitle: "",
      metaDescription: "",
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (landingPage) {
      reset({
        metaTitle: landingPage.metaTitle ?? "",
        metaDescription: landingPage.metaDescription ?? "",
      });
    }
  }, [landingPage, reset]);

  const onSubmit = async (data: SeoFormValues) => {
    if (!landingPage) return;
    setIsSaving(true);
    try {
      await shopApi.updateLandingPage(pageId, {
        name: landingPage.name,
        slug: landingPage.slug,
        isPublished: landingPage.isPublished,
        order: landingPage.order,
        metaTitle: data.metaTitle || undefined,
        metaDescription: data.metaDescription || undefined,
      });
      showSuccess(`Zapisano SEO dla "${name}"`);
      invalidate();
    } catch (error) {
      showError(`Nie udało się zapisać SEO dla "${name}"`);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
        <div className="mb-3">
          <p className="font-medium text-slate-800 dark:text-slate-100">{name}</p>
          <code className="text-xs text-slate-500">/{slug}</code>
        </div>
        {isLoading ? (
          <PaziaLoader position="center" />
        ) : (
          <div className="grid gap-3">
            <PaziaInput name="metaTitle" label="Meta title (SEO)" />
            <PaziaTextarea name="metaDescription" label="Meta description (SEO)" />
            <div>
              <PaziaFormButton disabled={isSaving} />
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

const SeoLandingPagesContent = () => {
  const { isLoading, landingPages } = useLandingPages(0, 200);

  if (isLoading) return <PaziaLoader position="center" />;

  if (landingPages.length === 0) {
    return <p className="text-slate-500">Brak stron do edycji.</p>;
  }

  return (
    <div className="grid gap-4 max-w-2xl">
      {landingPages.map((page) => (
        <SeoPageRow key={page.id} pageId={page.id} name={page.name} slug={page.slug} />
      ))}
    </div>
  );
};

type SeoSubTab = "pages" | "course-groups";

export const ShopSeoPage = () => {
  const [subTab, setSubTab] = useState<SeoSubTab>("pages");

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setSubTab("pages")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            subTab === "pages"
              ? "bg-primary-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
          }`}
        >
          SEO stron
        </button>
        <button
          type="button"
          onClick={() => setSubTab("course-groups")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            subTab === "course-groups"
              ? "bg-primary-600 text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
          }`}
        >
          SEO grup kursów
        </button>
      </div>
      {subTab === "pages" && <SeoLandingPagesContent />}
      {subTab === "course-groups" && <ShopSeoCourseGroupsPage />}
    </div>
  );
};
